import { useLanguage } from "@ihs-widget/utils/hooks/useLanguage";
import { useEffect, useRef, useState, useMemo } from "react";
import ScoreWithLabel from "@ihs-widget/components/StatsConsole/Widgets/StatsWidget/parts/GoalRow/ScoreWithLabel";
import { useColorContext } from "@ihs-widget/stores/ColorContext";

const CONSTANTS = {
    NET_ROWS: 4,
    SQUARE_WIDTH: 16.5,
    INITIAL_NET_SQUARES: 12
} as const;

interface GoalRowProps {
    homeShotsOnTarget: number;
    homeShotsOffTarget: number;
    awayShotsOnTarget: number;
    awayShotsOffTarget: number;
}

const GoalRow = ({ homeShotsOnTarget, homeShotsOffTarget, awayShotsOnTarget, awayShotsOffTarget }: GoalRowProps) => {
    const goalRowRef = useRef<HTMLDivElement>(null);
    const goalNetRef = useRef<HTMLDivElement>(null);
    const [netSquareQuantity, setNetSquareQuantity] = useState<number>(CONSTANTS.INITIAL_NET_SQUARES);
    const { colors, isDynamicColors } = useColorContext();

    const calculatePercent = (value: number, total: number): number => {
        if (total === 0) return 50;
        return Math.round((value / total) * 100);
    };

    const percentages = useMemo(() => ({
        onTarget: {
            home: calculatePercent(homeShotsOnTarget, homeShotsOnTarget + awayShotsOnTarget),
            away: calculatePercent(awayShotsOnTarget, homeShotsOnTarget + awayShotsOnTarget)
        },
        offTarget: {
            home: calculatePercent(homeShotsOffTarget, homeShotsOffTarget + awayShotsOffTarget),
            away: calculatePercent(awayShotsOffTarget, homeShotsOffTarget + awayShotsOffTarget)
        }
    }), [homeShotsOnTarget, awayShotsOnTarget, homeShotsOffTarget, awayShotsOffTarget]);

    const language = useLanguage();

    const tiles = useMemo(() => {
        return Array.from({ length: netSquareQuantity }, (_, index) => (
            <div 
                key={`tile-${index}`} 
                className={`w-[17.5px] ${
                    index === netSquareQuantity - 1 
                        ? '' 
                        : 'border-r-[0.5px] border-base090 dark:border-base0'
                }`}
            />
        ));
    }, [netSquareQuantity]);

    const netRows = useMemo(() => {
        return Array.from({ length: CONSTANTS.NET_ROWS }, (_, index) => (
            <div 
                key={`row-${index}`} 
                className={`flex flex-row justify-start w-full h-[16px] border-b-[0.5px] border-base090 dark:border-base0 z-20 ${
                    index === 0 ? 'mt-[4px]' : ''
                }`}
            >
                {tiles}
            </div>
        ));
    }, [tiles]);

    const GoalWithNet = () => {
        const { colors, isDynamicColors } = useColorContext();
        return (
            <div className={`absolute left-[17.5%] bottom-0 flex flex-row w-[65%] h-[68px] bg-transparent overflow-hidden 
                border-4 border-b-0 border-monoY dark:border-base0`}>
                <div className={`h-full bg-green-110 dark:bg-green-90 z-0`}
                    style={{ width: `${percentages.onTarget.home}%`, backgroundColor: `${colors?.homeColor}` }}
                ></div>
                <div className={`h-full bg-orange-90 dark:bg-yellow-90 z-0`}
                    style={{ width: `${percentages.onTarget.away}%`, backgroundColor: `${colors?.awayColor}` }}
                ></div>
                <Net />
            </div>
        )
    }

    const Net = () => {
        return (
            <div ref={goalNetRef} className={`absolute left-[0%] bottom-0 flex flex-col w-full h-[68px] 
                overflow-hidden z-10`}>
                {netRows}
            </div>
        )
    }

    useEffect(() => {
        if (goalNetRef.current) {
            const width = goalNetRef.current.getBoundingClientRect().width;
            const squareCount = Math.floor(width / CONSTANTS.SQUARE_WIDTH);
            setNetSquareQuantity(squareCount);
        }
    }, [goalNetRef.current, window.innerWidth]);

    return (
        <div ref={goalRowRef} 
            data-testid="goal-row-container"
            className={`relative flex flex-row w-full h-[116px] mt-[24px] rounded-[10px] bg-transparent overflow-hidden`}>
            <div 
                data-testid="goal-row-content"
                className="absolute left-[0%] top-[0%] w-full h-full flex flex-col items-start gap-[36px] mt-[12px]">
                <ScoreWithLabel
                    label={"InHouseStats.ShotsOffTarget"}
                    home={homeShotsOffTarget}
                    away={awayShotsOffTarget}
                    isTwoRows={false}
                />
                <ScoreWithLabel 
                    label={"InHouseStats.ShotsOnTarget"}
                    home={homeShotsOnTarget}
                    away={awayShotsOnTarget}
                    isTwoRows={true}
                />
            </div>
            <div className={`h-full`}
                style={{ width: `${percentages.offTarget.home}%`, backgroundColor: `${colors?.homeColor}` }}
            ></div>
            <div className={`h-full`}
                style={{ width: `${percentages.offTarget.away}%`, backgroundColor: `${colors?.awayColor}` }}
            ></div>
            <GoalWithNet />
        </div>
    )
}

export default GoalRow;
